<script setup lang="ts">
import { defineProps, useAttrs } from "vue";
import { useSkillsState } from "@/composables/useSkillsState";

// Props declaration
export interface Props {
  skillClass?: string; // Optional class for styling skill text
  skills: string[]; // List of skills to display
}

// Destructure props
const { skillClass, skills } = defineProps<Props>();
const attrs = useAttrs(); // Pass additional attributes

// Use the skills state composable
const { selectedSkills, toggleSkill } = useSkillsState();

function handleSkillToggle(skill: string) {
  toggleSkill(skill);
}

function handleKeydown(e: KeyboardEvent, skill: string) {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    toggleSkill(skill);
  }
}
</script>

<template>
  <div v-bind="attrs">
    <button
        v-for="skill in skills"
        :key="skill"
        type="button"
        @click="handleSkillToggle(skill)"
        @keydown="handleKeydown($event, skill)"
        :aria-pressed="selectedSkills.includes(skill)"
        :aria-label="`Filter by ${skill}${selectedSkills.includes(skill) ? ' (active)' : ''}`"
        :class="[
        'skills-container cursor-pointer rounded-md px-2.5 py-1.5 text-left transition-colors inline-block mr-1.5 mb-1.5',
        selectedSkills.includes(skill)
          ? 'border border-cyan-600 bg-cyan-100 ring-1 ring-cyan-600 dark:border-cyan-400 dark:bg-cyan-950'
          : 'border border-slate-300 bg-white hover:border-cyan-500 hover:text-cyan-800 dark:border-slate-600 dark:bg-slate-800 dark:hover:border-cyan-400'
      ]"
    >
      <span :class="[
        skillClass,
        'text-xs font-semibold',
        selectedSkills.includes(skill)
          ? 'text-cyan-900 dark:text-cyan-200'
          : 'text-slate-700 dark:text-slate-200'
      ]">
        {{ skill }}
      </span>
    </button>
  </div>
</template>

<style scoped>
.skills-container {
  transition-duration: 150ms;
}

.skills-container:hover {
  transform: translateY(-1px);
}

@media (prefers-reduced-motion: reduce) {
  .skills-container { transition: none; }
  .skills-container:hover { transform: none; }
}
</style>
